import { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { LinearProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import NRTaskProgressStyles from './NRTaskProgress.styles';
import { CONSTANTS } from '../../constants/constants';
const { ERROR, IN_PROGRESS, SUCCESS, ABORTED } = CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS;
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import NRActionModal from '../NRActionModal/NRActionModal';
import { httpFileDownload } from '../../utils/httpFileDownload';
import { useAuth0 } from '@auth0/auth0-react';
import { useRef } from 'react';
import { humanFileSize } from '../../utils/utils';
import { AssetsContext } from '../../DataShare/NRAssetsDataShare';
import { StatusIcon } from './NRTaskIndeterminateProgress';
const { TITLE_S, CANCEL_S, CONTINUE_S, MESSAGE_S } = CONSTANTS.TRANSACTION_ASSET.CONFIRMATION_MODAL;

const useStyles = makeStyles(NRTaskProgressStyles);

function NRHttpDownloadTask({ task }) {
  const classes = useStyles();
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const { updateStatus } = useContext(AssetsContext);
  const { getAccessTokenSilently } = useAuth0();
  const { id } = task;

  const controllerRef = useRef(null);
  const startedTaskIdRef = useRef(null);

  useEffect(() => {
    const startDownload = async () => {
      const token = await getAccessTokenSilently();
      const controller = new AbortController();
      controllerRef.current = controller;

      httpFileDownload(
        token,
        task?.downloadUrl,
        task?.body,
        task?.headers,
        res => {
          task?.onSuccess?.(res);
          updateStatus(SUCCESS, null, task?.downloadUrl);
        },
        res => {
          task?.onError?.(res);
          updateStatus(ERROR, null, task?.downloadUrl);
        },
        task?.errorMessage,
        task?.fileName,
        loaded => setDownloadProgress(loaded),
        controller.signal
      );
      updateStatus(IN_PROGRESS, null, task?.downloadUrl);
    };

    // StrictMode re-runs effects in dev and the token await means the cleanup below fires before
    // controllerRef is set, so without this guard the request and its download happen twice.
    if (startedTaskIdRef.current !== id) {
      startedTaskIdRef.current = id;
      startDownload();
    }

    return () => {
      controllerRef.current?.abort();
    };
  }, [id]);

  const handleClose = () => {
    setOpenModal(false);
  };

  const continueUpload = () => {
    setOpenModal(false);
  };

  const cancelUpload = () => {
    controllerRef.current?.abort();
    updateStatus(ABORTED, null, task?.downloadUrl);
    setOpenModal(false);
  };

  return (
    <Box>
      <NRActionModal
        title={TITLE_S}
        actionLabel={CANCEL_S}
        cancelLabel={CONTINUE_S}
        content={MESSAGE_S}
        open={openModal}
        handleClose={handleClose}
        handleCancelAction={continueUpload}
        handleConfirmAction={cancelUpload}
        actionButtonProps={{ variant: 'contained', color: 'secondary' }}
        cancelButtonProps={{ variant: 'outlined', color: 'secondary' }}
        width={410}
      />
      <div className={classes.masterDiv} id={`${task.id}-container`}>
        <div className={classes.container}>
          <div className={classes.uploadDataContainer}>
            <div className={classes.statusIcon}>
              <StatusIcon status={task.status} download={true} />
            </div>
            <div
              className={clsx({
                [classes.assetDataContainer]: task.status === IN_PROGRESS,
                [classes.assetDataContainerNoAction]: task.status !== IN_PROGRESS
              })}
            >
              <div className={classes.nameContainer}>
                <div className={classes.name}>{task?.fileName}</div>
              </div>
              {task.status === SUCCESS && <p className={classes.remainingTime}>Downloaded via browser</p>}
              {task.status === IN_PROGRESS && (
                <p className={classes.remainingTime}>
                  {downloadProgress === 0 ? 'Generating file for download...' : `Downloading file ${humanFileSize(downloadProgress)}`}
                </p>
              )}
              {task.status === ERROR && <p className={classes.remainingTime}>Failed</p>}
              {task.status === ABORTED && <p className={classes.remainingTime}>Canceled</p>}
            </div>
            {task.status === IN_PROGRESS && (
              <div className={classes.uploadActionContainer}>
                <div className={classes.buttonContainer}>
                  <IconButton aria-label="Close" onClick={() => setOpenModal(true)} size="small">
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            )}
          </div>
        </div>
        {task.status === IN_PROGRESS && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="indeterminate"
              classes={{ colorPrimary: classes.colorPrimaryProgressBar, barColorPrimary: classes.barColorPrimaryProgressBar }}
            />
          </Box>
        )}
      </div>
    </Box>
  );
}

export default NRHttpDownloadTask;
